import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useDecks } from '../../hooks/useDecks';
import { useFormats } from '../../hooks/useFormats';

const DeckManager: React.FC = () => {
  const [deckName, setDeckName] = useState('');
  const [selectedFormatId, setSelectedFormatId] = useState('');
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const { decks, addDeck, deleteDeck } = useDecks();
  const { formats } = useFormats();

  const handleAddDeck = async () => {
    setError('');
    
    if (!deckName.trim()) {
      setError('デッキ名を入力してください');
      return;
    }
    
    if (!selectedFormatId) {
      setError('フォーマットを選択してください');
      return;
    }
    
    try {
      await addDeck(deckName.trim(), selectedFormatId);
      setDeckName('');
      setSelectedFormatId('');
      setError('');
      setOpen(false);
    } catch (error) {
      setError('デッキの追加に失敗しました');
    }
  };

  const handleDeleteDeck = async (deckId: string) => {
    if (window.confirm('このデッキを削除しますか？')) {
      try {
        await deleteDeck(deckId);
      } catch (error) {
        setError('デッキの削除に失敗しました');
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          デッキ管理
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          デッキ追加
        </Button>
      </Box>

      <Paper elevation={2}>
        {decks.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              まだデッキが登録されていません
            </Typography>
          </Box>
        ) : (
          <List>
            {decks.map((deck, index) => {
              const format = formats.find(f => f.id === deck.formatId);
              return (
                <ListItem key={deck.id} divider={index < decks.length - 1}>
                  <ListItemText 
                    primary={deck.name} 
                    secondary={format ? `フォーマット: ${format.name}` : 'フォーマット不明'}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteDeck(deck.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>新しいデッキを追加</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="デッキ名"
            fullWidth
            variant="outlined"
            value={deckName}
            onChange={(e) => setDeckName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel>フォーマット *</InputLabel>
            <Select
              value={selectedFormatId}
              onChange={(e) => setSelectedFormatId(e.target.value)}
              label="フォーマット *"
            >
              {formats.map((format) => (
                <MenuItem key={format.id} value={format.id}>
                  {format.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>キャンセル</Button>
          <Button onClick={handleAddDeck} variant="contained">
            追加
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeckManager;